<template>
  <!-- Tela cheia: o mesmo conteúdo passa a ser renderizado dentro de um v-dialog fullscreen. -->
  <component :is="fullscreen ? 'v-dialog' : 'div'" v-bind="wrapperAttrs" v-on="wrapperListeners">
  <div
    class="linha-comparacao-rotas d-flex flex-column pa-2"
    :class="{ 'linha-comparacao-rotas--fullscreen white': fullscreen }"
  >
    <template v-if="!linha || !linha.id_linha">
      <v-alert type="info" dense outlined>{{ $t('crudLinhas.detail.selectResource') }}</v-alert>
    </template>
    <template v-else-if="idSentido == null">
      <v-alert type="info" dense outlined>{{ $t('crudLinhas.detail.comparison.selectDirection') }}</v-alert>
    </template>
    <template v-else>
      <div class="d-flex align-center flex-wrap mb-2 flex-grow-0">
        <!-- Em tela cheia o seletor do topo do painel fica oculto: o sentido é trocado aqui. -->
        <v-select
          v-if="fullscreen"
          :value="idSentido"
          :items="sentidosComparaveis"
          item-text="descricao_sentido"
          item-value="id_sentido"
          :label="$t('crudLinhas.detail.comparison.direction')"
          class="linha-comparacao-rotas__sentido mr-4"
          dense
          outlined
          hide-details
          @change="$emit('update:sentidoSelecionado', $event)"
        />
        <span v-else class="subtitle-2 mr-4">
          {{ $t('crudLinhas.detail.comparison.direction') }}: {{ descricaoSentido }}
        </span>
        <v-spacer />
        <!-- Navegação entre as viagens realizadas: 1 = a última -->
        <div class="d-flex align-center mr-2">
          <v-btn small icon :disabled="loading || !temViagemAnterior" :title="t('olderTrip')" @click="mudaViagem(1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="caption mx-1">{{ t('tripNumber', { n: indiceViagem + 1 }) }}</span>
          <v-btn small icon :disabled="loading || indiceViagem === 0" :title="t('newerTrip')" @click="mudaViagem(-1)">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn small icon :disabled="loading || indiceViagem === 0" :title="t('latestTrip')" @click="mudaViagem(-indiceViagem)">
            <v-icon>mdi-page-last</v-icon>
          </v-btn>
        </div>
        <v-btn small text color="primary" class="mr-2" :disabled="loading" @click="load">
          <v-icon left small>mdi-refresh</v-icon>
          {{ $t('crudLinhas.detail.comparison.refresh') }}
        </v-btn>
        <v-btn v-if="podeVerHistorico" small text color="primary" class="mr-2" @click="abreHistorico">
          <v-icon left small>mdi-history</v-icon>
          {{ t('history') }}
        </v-btn>
        <v-btn
          small
          :text="!sobrepor"
          :outlined="sobrepor"
          color="primary"
          class="mr-2"
          :title="t('overlayHint')"
          @click="sobrepor = !sobrepor"
        >
          <v-icon left small>{{ sobrepor ? 'mdi-view-column' : 'mdi-layers-outline' }}</v-icon>
          {{ sobrepor ? t('sideBySide') : t('overlay') }}
        </v-btn>
        <v-btn
          v-if="canEdit"
          small
          color="primary"
          :loading="gerando"
          :disabled="gerando"
          @click="gerarOtimizacao"
        >
          <v-icon left small>mdi-map-marker-path</v-icon>
          {{ $t('crudLinhas.detail.comparison.generate') }}
        </v-btn>
        <v-btn
          small
          icon
          class="ml-2"
          :title="fullscreen ? t('exitFullscreen') : t('fullscreen')"
          @click="fullscreen = !fullscreen"
        >
          <v-icon>{{ fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
        </v-btn>
      </div>
      <v-alert v-if="gerando" type="info" dense outlined class="flex-grow-0">
        {{ $t('crudLinhas.detail.comparison.generating') }}
      </v-alert>
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2 flex-grow-0" />
      <!-- Sobreposição: tarifas no mesmo formato de indicadores do card -->
      <v-card v-if="sobrepor" outlined class="mb-2 flex-grow-0">
        <v-card-text class="pt-1 pb-2">
          <div class="subtitle-2">{{ t("costTariffs") }}</div>
          <div v-if="carregandoCusto" class="body-2 text--secondary">{{ t("costLoading") }}</div>
          <div v-else-if="!custoParams" class="body-2 text--secondary">{{ t("costUnavailable") }}</div>
          <v-row v-else dense>
            <v-col
              v-for="info in tarifasItens"
              :key="info.label"
              cols="6"
              sm="4"
              md
              class="linha-comparacao-rotas__indicador"
            >
              <div class="body-2 text--secondary">{{ info.label }}</div>
              <div class="title">{{ info.valor }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <div v-else class="caption text--secondary mb-2 flex-grow-0">
        <span class="font-weight-medium">{{ t("costTariffs") }}:</span>
        <template v-if="carregandoCusto">{{ t("costLoading") }}</template>
        <template v-else-if="!custoParams">{{ t("costUnavailable") }}</template>
        <template v-else>{{ tarifasLabel }}</template>
      </div>

      <v-row dense>
        <v-col v-for="coluna in colunasVisiveis" :key="coluna.key" cols="12" :md="sobrepor ? 12 : 4">
          <v-card outlined class="fill-height d-flex flex-column">
            <v-card-title class="subtitle-1" :class="coluna.legenda ? 'pt-1 pb-1' : 'py-2'">
              <span
                v-if="!coluna.legenda"
                class="linha-comparacao-rotas__swatch mr-2"
                :style="{ background: coluna.cor }"
              />
              {{ coluna.titulo }}
            </v-card-title>
            <!-- Sobreposição: um indicador por coluna, com valor em destaque -->
            <v-card-text v-if="coluna.legenda" class="pt-0 pb-1">
              <v-row dense>
                <v-col
                  v-for="info in coluna.infos"
                  :key="info.label"
                  cols="6"
                  sm="4"
                  md
                  class="linha-comparacao-rotas__indicador"
                >
                  <div class="body-2 text--secondary">{{ info.label }}</div>
                  <div class="title">{{ info.valor }}</div>
                </v-col>
              </v-row>
              <div v-if="coluna.aviso" class="body-2 orange--text text--darken-2 mt-1">{{ coluna.aviso }}</div>
            </v-card-text>
            <v-card-text v-else class="pb-2 linha-comparacao-rotas__info">
              <div v-for="info in coluna.infos" :key="info.label">
                <span class="font-weight-medium">{{ info.label }}:</span> {{ info.valor }}
              </div>
              <div v-if="coluna.aviso" class="orange--text text--darken-2">{{ coluna.aviso }}</div>
            </v-card-text>
            <div
              class="linha-comparacao-rotas__map"
              :class="{ 'linha-comparacao-rotas__map--sobreposto': sobrepor }"
            >
              <l-map
                :ref="'map_' + coluna.key"
                :zoom="12"
                :center="centroPadrao"
                :options="{ zoomControl: true, attributionControl: false }"
                style="height: 100%; width: 100%"
                @ready="ajustaMapa(coluna.key)"
              >
                <l-tile-layer :url="tileUrl" />
                <l-geo-json
                  v-for="(geo, i) in coluna.geojsons"
                  :key="coluna.key + '_geo_' + (geo.id || i)"
                  :geojson="geo.geojson"
                  :options-style="geo.style"
                />
                <l-polyline
                  v-if="coluna.polyline.length"
                  :lat-lngs="coluna.polyline"
                  :color="coluna.cor"
                  :weight="4"
                />
                <l-circle-marker
                  v-for="(p, i) in coluna.paradas"
                  :key="coluna.key + '_p_' + (p.id_cerca != null ? p.id_cerca : i)"
                  :lat-lng="[p.lat, p.lng]"
                  :radius="p.terminal ? 7 : 5"
                  :color="p.terminal ? '#212121' : p.cor || coluna.cor"
                  :fill-color="p.terminal ? '#212121' : '#ffffff'"
                  :fill-opacity="1"
                  :weight="2"
                >
                  <l-tooltip>{{ p.label }}</l-tooltip>
                </l-circle-marker>
              </l-map>
              <div v-if="coluna.vazio" class="linha-comparacao-rotas__vazio caption">
                {{ coluna.vazio }}
              </div>
              <!-- Fora do l-map: cliques nos switches não arrastam nem dão zoom no mapa -->
              <v-card v-if="coluna.legenda" class="linha-comparacao-rotas__legenda pa-2" elevation="3">
                <div class="caption font-weight-medium mb-1">{{ t("legend") }}</div>
                <div v-for="item in coluna.legenda" :key="item.camada">
                  <v-switch
                    v-model="camadasSobreposicao[item.camada]"
                    :color="item.cor"
                    class="mt-0 pt-0 linha-comparacao-rotas__legenda-switch"
                    dense
                    inset
                    hide-details
                  >
                    <template #label>
                      <span
                        class="mr-2"
                        :class="item.ponto ? 'linha-comparacao-rotas__ponto' : 'linha-comparacao-rotas__swatch'"
                        :style="estiloSwatch(item)"
                      />
                      <span class="caption">{{ item.titulo }}</span>
                    </template>
                  </v-switch>
                  <div
                    v-if="item.extra && camadasSobreposicao[item.camada]"
                    class="d-flex align-center caption linha-comparacao-rotas__legenda-extra"
                  >
                    <span
                      class="linha-comparacao-rotas__ponto mr-2"
                      :style="{ borderColor: item.extra.cor }"
                    />
                    {{ item.extra.titulo }}
                  </div>
                </div>
              </v-card>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
  </component>
</template>

<script>
import L from "leaflet";
import { VDialog } from "vuetify/lib";
import { LMap, LTileLayer, LGeoJson, LPolyline, LCircleMarker, LTooltip } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";
import { computeRouteCost } from "@/utils/historicoAlteracoesRotas";
import { carregaTarifasCustoLinha } from "@/utils/custoLinhaContrato";
import { podeVerHistoricoRotasOtimizadas, rotaHistoricoRotasOtimizadas } from "@/utils/historicoRotasOtimizadas";

const COR_REALIZADA = "#1976D2";
const COR_ESPERADA = "#2E7D32";
const COR_OTIMIZADA = "#8E24AA";
// Sobreposição: par âmbar × azul contrasta entre si e com o mapa em cinza (seguro para daltonismo)
const COR_SOBREPOSTA_OTIMIZADA = "#FFB300";
const COR_SOBREPOSTA_REALIZADA = "#0D47A1";
const COR_SOBREPOSTA_ESPERADA = "#C2185B";
const COR_SOBREPOSTA_PARADA = "#455A64";
const COR_NAO_ATENDIDA = "#D50000";
const TIPO_INICIO = 2;
const TIPO_FIM = 3;
const TIPO_ROTA_DESLOCAMENTO = 2;
const POLL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;
/** A geração leva segundos; PENDING por mais que isso indica que o scheduler não está rodando. */
const PENDING_TIMEOUT_MS = 2 * 60 * 1000;
// Mesmo teto do km elegível do subsídio: km aferido acima disso é leitura quebrada
const KM_AFERIDO_MAX = 1000;

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Comprimento em metros das linhas de um GeoJSON (LineString/MultiLineString, Feature ou coleção). */
function comprimentoGeojson(geojson) {
  let total = 0;
  try {
    L.geoJSON(geojson).eachLayer((layer) => {
      if (typeof layer.getLatLngs !== "function") return;
      const partes = layer.getLatLngs();
      const linhas = Array.isArray(partes[0]) ? partes : [partes];
      linhas.forEach((pts) => {
        for (let i = 1; i < pts.length; i++) total += pts[i - 1].distanceTo(pts[i]);
      });
    });
  } catch (_) {
    return 0;
  }
  return total;
}

export default {
  name: "LinhaComparacaoRotas",
  // VDialog explícito: o vuetify-loader não registra componentes usados só via <component :is>.
  components: { VDialog, LMap, LTileLayer, LGeoJson, LPolyline, LCircleMarker, LTooltip },
  props: {
    linha: { type: Object, default: null },
    sentidos: { type: Array, default: () => [] },
    sentidoSelecionado: { type: [Number, String], default: null },
    canEdit: { type: Boolean, default: false },
    /** Aba visível: só carrega/redimensiona os mapas quando o usuário está olhando. */
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      centroPadrao: [-23.55, -46.63],
      loading: false,
      /** Viagem realizada exibida: 0 = a última, 1 = a anterior a ela... */
      indiceViagem: 0,
      dados: null,
      carregadoPara: null,
      gerando: false,
      pollTimer: null,
      pollInFlight: false,
      requestSeq: 0,
      custoParams: null,
      carregandoCusto: false,
      fullscreen: false,
      podeVerHistorico: podeVerHistoricoRotasOtimizadas(),
      /** Esperada e otimizada num único mapa, sobrepostas. */
      sobrepor: false,
      /** Camadas ligadas no mapa sobreposto (switches da legenda). */
      camadasSobreposicao: { esperada: true, otimizada: true, realizada: true, paradas: true },
    };
  },
  computed: {
    wrapperAttrs() {
      return this.fullscreen
        ? { value: true, fullscreen: true, hideOverlay: true, transition: "dialog-bottom-transition" }
        : {};
    },
    wrapperListeners() {
      // Esc / fechamento do dialog volta para a aba
      return this.fullscreen ? { input: (val) => { this.fullscreen = !!val; } } : {};
    },
    /** Comparação é por sentido: "ambos" (-1) cai no primeiro sentido real cadastrado. */
    idSentido() {
      const atual = toNumber(this.sentidoSelecionado);
      if (atual != null && atual !== -1) return atual;
      const primeiro = (this.sentidos || []).find((s) => Number(s.id_sentido) !== -1);
      return primeiro ? Number(primeiro.id_sentido) : null;
    },
    /** "Ambos" (-1) não é comparável: o seletor só oferece sentidos reais. */
    sentidosComparaveis() {
      return (this.sentidos || []).filter((s) => Number(s.id_sentido) !== -1);
    },
    descricaoSentido() {
      const s = (this.sentidos || []).find((x) => Number(x.id_sentido) === this.idSentido);
      return s ? s.descricao_sentido : this.idSentido;
    },
    chaveCarga() {
      return this.linha && this.linha.id_linha && this.idSentido != null
        ? `${this.linha.id_linha}_${this.idSentido}`
        : null;
    },
    temViagemAnterior() {
      return !!(this.dados && this.dados.realizada && this.dados.realizada.tem_anterior);
    },
    colunas() {
      return [this.colunaRealizada(), this.colunaEsperada(), this.colunaOtimizada()];
    },
    colunasVisiveis() {
      return this.sobrepor ? [this.colunaSobreposta()] : this.colunas;
    },
    /** Tarifas usadas: origem, tipo de veículo e valores do contrato (ou padrão de simulação). */
    tarifasLabel() {
      const p = this.custoParams;
      if (!p) return "—";
      const fonte = p.fonte === "contrato" ? this.t("costFromContract") : this.t("costFromDefaults");
      const cap = Number(p.capacidade);
      const tipo = p.tipo ? ` · ${p.tipo}${Number.isFinite(cap) && cap > 0 ? ` (${cap})` : ""}` : "";
      return `${fonte}${tipo} — ${this.t("costPerKm")}: ${this.formataMoeda(p.costPerKm)} · `
        + `${this.t("costFixed")}: ${this.formataMoeda(p.fixedCost)} · `
        + `${this.t("costLabor")}: ${this.formataMoeda(p.laborCost)}`;
    },
    /** Mesmas tarifas de tarifasLabel, uma por indicador. */
    tarifasItens() {
      const p = this.custoParams;
      if (!p) return [];
      const cap = Number(p.capacidade);
      return [
        { label: this.t("costSource"), valor: p.fonte === "contrato" ? this.t("costFromContract") : this.t("costFromDefaults") },
        ...(p.tipo
          ? [{ label: this.t("costVehicleType"), valor: `${p.tipo}${Number.isFinite(cap) && cap > 0 ? ` (${cap})` : ""}` }]
          : []),
        { label: this.t("costPerKmLabel"), valor: this.formataMoeda(p.costPerKm) },
        { label: this.t("costFixedLabel"), valor: this.formataMoeda(p.fixedCost) },
        { label: this.t("costLaborLabel"), valor: this.formataMoeda(p.laborCost) },
      ];
    },
    /** Km de cada versão do trajeto: base do custo variável. */
    kmPorColuna() {
      const d = this.dados;
      if (!d) return {};
      const viagem = d.realizada && d.realizada.viagem;
      let realizada = null;
      if (viagem) {
        const aferido = toNumber(viagem.km_aferido);
        if (aferido != null && aferido > 0 && aferido <= KM_AFERIDO_MAX) realizada = aferido;
        else if ((d.realizada.posicoes || []).length > 1) {
          realizada = comprimentoGeojson({
            type: "LineString",
            coordinates: d.realizada.posicoes.map(([lat, lng]) => [lng, lat]),
          }) / 1000;
        }
      }
      const rotas = (d.esperada && d.esperada.rotas) || [];
      const esperadaM = rotas
        .filter((r) => r.geojson && Number(r.id_tipo_rota) !== TIPO_ROTA_DESLOCAMENTO)
        .reduce((acc, r) => acc + comprimentoGeojson(r.geojson), 0);
      const otimizadaM = d.otimizada && d.otimizada.geojson ? toNumber(d.otimizada.distancia) : null;
      return {
        realizada,
        esperada: esperadaM ? esperadaM / 1000 : null,
        otimizada: otimizadaM ? otimizadaM / 1000 : null,
      };
    },
    /** Custo estimado por viagem de cada versão: fixo + mão de obra + variável × km. */
    custos() {
      const p = this.custoParams;
      if (!p) return {};
      const out = {};
      Object.entries(this.kmPorColuna).forEach(([key, km]) => {
        out[key] = km != null ? computeRouteCost(km, p) : null;
      });
      return out;
    },
  },
  watch: {
    chaveCarga() {
      this.indiceViagem = 0;
      this.dados = null;
      this.carregadoPara = null;
      this.custoParams = null;
      if (this.active) this.load();
    },
    fullscreen() {
      // Os mapas são recriados ao trocar de container: reenquadra após a transição
      this.ajustaTodos();
    },
    sobrepor() {
      this.ajustaTodos();
    },
    active(val) {
      if (!val) {
        this.fullscreen = false;
        return;
      }
      if (this.carregadoPara !== this.chaveCarga) this.load();
      else this.ajustaTodos();
    },
  },
  mounted() {
    if (this.active) this.load();
  },
  beforeDestroy() {
    this.clearPoll();
  },
  methods: {
    t(key, args) {
      return this.$t(`crudLinhas.detail.comparison.${key}`, args);
    },
    abreHistorico() {
      this.fullscreen = false;
      this.$router.push(rotaHistoricoRotasOtimizadas("linha", this.linha.id_linha, this.idSentido));
    },
    estiloSwatch(item) {
      if (item.ponto) return { borderColor: item.cor };
      if (item.tracejada) {
        return { background: `repeating-linear-gradient(90deg, ${item.cor} 0 4px, transparent 4px 7px)` };
      }
      return { background: item.cor };
    },
    formataMoeda(valor) {
      const n = toNumber(valor);
      if (n == null) return "—";
      const isEs = this.$i18n && String(this.$i18n.locale).trim() === "es-MX";
      return n.toLocaleString(isEs ? "es-MX" : "pt-BR", {
        style: "currency",
        currency: isEs ? "MXN" : "BRL",
      });
    },
    /** Linhas de custo de uma coluna; realizada e otimizada mostram a diferença para a esperada. */
    infosCusto(key) {
      if (!this.custoParams || !this.dados) return [];
      const custo = this.custos[key];
      if (!custo) return [];
      const infos = [
        { label: this.t("cost"), valor: this.formataMoeda(custo.custo_total) },
        { label: this.t("costVariable"), valor: this.formataMoeda(custo.custo_variavel) },
      ];
      const base = this.custos.esperada;
      if (key !== "esperada" && base) {
        const diff = Number((custo.custo_total - base.custo_total).toFixed(2));
        infos.push({
          label: this.t("costDiffExpected"),
          valor: `${diff > 0 ? "+" : ""}${this.formataMoeda(diff)}`,
        });
      }
      return infos;
    },
    async carregaCusto(chave) {
      this.carregandoCusto = true;
      try {
        const lotacao = toNumber(this.linha && this.linha.lotacao_prevista) || 0;
        const alvo = Math.max(lotacao, this.cercasEsperadas().length) || null;
        const params = await carregaTarifasCustoLinha(this.linha, alvo);
        if (chave === this.chaveCarga) this.custoParams = params;
      } catch (_) {
        if (chave === this.chaveCarga) this.custoParams = null;
      } finally {
        if (chave === this.chaveCarga) this.carregandoCusto = false;
      }
    },
    formataKm(metros) {
      const m = toNumber(metros);
      return m == null ? "—" : `${(m / 1000).toFixed(1)} km`;
    },
    formataDuracao(segundos) {
      const s = toNumber(segundos);
      if (s == null) return "—";
      const h = Math.floor(s / 3600);
      const min = Math.round((s % 3600) / 60);
      return h ? `${h} h ${min} min` : `${min} min`;
    },
    paradaLabel(p, ordem) {
      return [ordem != null ? `${ordem}.` : null, p.nome_cerca || `#${p.id_cerca}`].filter(Boolean).join(" ");
    },
    cercasEsperadas() {
      const cercas = (this.dados && this.dados.esperada && this.dados.esperada.cercas) || [];
      return cercas
        .map((c) => ({
          ...c,
          lat: toNumber(c.lat),
          lng: toNumber(c.lng),
          terminal: [TIPO_INICIO, TIPO_FIM].includes(Number(c.id_tipo_cerca)),
          label: this.paradaLabel(c, c.ordem_parada),
        }))
        .filter((c) => c.lat != null && c.lng != null);
    },
    nomesCercas() {
      const map = {};
      for (const c of this.cercasEsperadas()) map[c.id_cerca] = c.nome_cerca;
      return map;
    },
    colunaRealizada() {
      const r = this.dados && this.dados.realizada;
      const viagem = r && r.viagem;
      const posicoes = (r && r.posicoes) || [];
      const infos = viagem
        ? [
            { label: this.t("vehicle"), valor: viagem.cod_veiculo || viagem.numero_ordem || viagem.id_veiculo_realizado },
            { label: this.t("departure"), valor: viagem.horario_saida_realizada_extenso },
            { label: this.t("arrival"), valor: viagem.horario_chegada_realizada_extenso },
            { label: this.t("measuredKm"), valor: viagem.km_aferido != null ? viagem.km_aferido : "—" },
            ...this.infosCusto("realizada"),
          ]
        : [];
      let vazio = null;
      if (this.dados && !viagem) vazio = this.t("noTrip", { dias: (r && r.dias_busca) || 60 });
      else if (this.dados && !posicoes.length) vazio = this.t("noPositions");
      return {
        key: "realizada",
        titulo: this.t("realized"),
        cor: COR_REALIZADA,
        infos,
        geojsons: [],
        polyline: posicoes,
        paradas: this.cercasEsperadas(),
        vazio,
      };
    },
    colunaEsperada() {
      const rotas = (this.dados && this.dados.esperada && this.dados.esperada.rotas) || [];
      const comGeo = rotas.filter((r) => r.geojson);
      const principais = comGeo.filter((r) => Number(r.id_tipo_rota) !== TIPO_ROTA_DESLOCAMENTO);
      // rotas.distancia não é confiável (há rotas com 0 ou valores antigos): mede a geometria
      const distancia = principais.reduce((acc, r) => acc + comprimentoGeojson(r.geojson), 0);
      const infos = comGeo.length
        ? [
            { label: this.t("routeType"), valor: comGeo.map((r) => `${r.cod_rota || r.id_rota} (${r.descricao_tipo_rota || r.id_tipo_rota})`).join(", ") },
            { label: this.t("distance"), valor: distancia ? this.formataKm(distancia) : "—" },
            { label: this.t("stops"), valor: this.cercasEsperadas().length },
            ...this.infosCusto("esperada"),
          ]
        : [];
      return {
        key: "esperada",
        titulo: this.t("expected"),
        cor: COR_ESPERADA,
        infos,
        geojsons: comGeo.map((r) => ({
          geojson: r.geojson,
          style: () => ({
            color: COR_ESPERADA,
            weight: 4,
            dashArray: Number(r.id_tipo_rota) === TIPO_ROTA_DESLOCAMENTO ? "6 6" : null,
          }),
        })),
        polyline: [],
        paradas: this.cercasEsperadas(),
        vazio: this.dados && !comGeo.length ? this.t("noExpected") : null,
      };
    },
    colunaOtimizada() {
      const o = this.dados && this.dados.otimizada;
      const nomes = this.nomesCercas();
      const paradas = ((o && o.paradas_json) || [])
        .map((p) => ({
          ...p,
          lat: toNumber(p.lat),
          lng: toNumber(p.lng),
          terminal: [TIPO_INICIO, TIPO_FIM].includes(Number(p.id_tipo_cerca)),
          label: this.paradaLabel({ ...p, nome_cerca: nomes[p.id_cerca] }, p.ordem_otimizada),
        }))
        .filter((p) => p.lat != null && p.lng != null);
      const naoAtendidas = (o && o.nao_atendidas_json) || [];
      const infos = o
        ? [
            { label: this.t("status"), valor: o.status },
            { label: this.t("distance"), valor: this.formataKm(o.distancia) },
            { label: this.t("duration"), valor: this.formataDuracao(o.duracao) },
            { label: this.t("stops"), valor: o.qtd_paradas != null ? o.qtd_paradas : "—" },
            { label: this.t("generatedAt"), valor: o.dt_geracao_extenso },
            ...this.infosCusto("otimizada"),
          ]
        : [];
      let vazio = null;
      if (this.dados && !o) vazio = this.t("noOptimized");
      else if (o && !o.geojson) vazio = o.mensagem || o.status;
      return {
        key: "otimizada",
        titulo: this.t("optimized"),
        cor: COR_OTIMIZADA,
        infos,
        aviso: naoAtendidas.length ? this.t("unassigned", { count: naoAtendidas.length }) : null,
        geojsons: o && o.geojson
          ? [{ geojson: o.geojson, style: () => ({ color: COR_OTIMIZADA, weight: 4 }) }]
          : [],
        polyline: [],
        paradas,
        vazio,
      };
    },
    /**
     * Esperada, otimizada e realizada no mesmo mapa: a otimizada vira uma faixa larga e translúcida
     * por baixo, a realizada uma linha cheia e a esperada um tracejado por cima. Onde coincidem, as
     * linhas correm dentro da faixa; onde divergem, aparecem separadas.
     */
    colunaSobreposta() {
      const realizada = this.colunaRealizada();
      const esperada = this.colunaEsperada();
      const otimizada = this.colunaOtimizada();
      const rotasEsperadas = ((this.dados && this.dados.esperada && this.dados.esperada.rotas) || [])
        .filter((r) => r.geojson);
      const o = this.dados && this.dados.otimizada;
      const visivel = this.camadasSobreposicao;
      // Ordem de desenho: faixa larga por baixo, linha cheia no meio, tracejado por cima
      const geojsons = [
        ...(o && o.geojson
          ? [{
              id: "otimizada",
              camada: "otimizada",
              geojson: o.geojson,
              style: () => ({
                color: COR_SOBREPOSTA_OTIMIZADA,
                weight: 12,
                opacity: 0.75,
                lineCap: "round",
                lineJoin: "round",
              }),
            }]
          : []),
        // Realizada como GeoJSON (não l-polyline) para respeitar a ordem acima
        ...(realizada.polyline.length > 1
          ? [{
              id: "realizada",
              camada: "realizada",
              geojson: {
                type: "LineString",
                coordinates: realizada.polyline.map(([lat, lng]) => [lng, lat]),
              },
              style: () => ({ color: COR_SOBREPOSTA_REALIZADA, weight: 4, opacity: 1 }),
            }]
          : []),
        ...rotasEsperadas.map((r, i) => ({
          id: `esperada_${r.id_rota != null ? r.id_rota : i}`,
          camada: "esperada",
          geojson: r.geojson,
          style: () => ({
            color: COR_SOBREPOSTA_ESPERADA,
            weight: 3,
            opacity: 1,
            // Deslocamento pontilhado para não se confundir com o trajeto principal
            dashArray: Number(r.id_tipo_rota) === TIPO_ROTA_DESLOCAMENTO ? "2 8" : "6 6",
          }),
        })),
      ];

      // Mesma cerca nas duas versões: um marcador só, com as duas ordens no rótulo
      const porCerca = new Map();
      esperada.paradas.forEach((p) => {
        porCerca.set(String(p.id_cerca), { ...p, ordemEsperada: p.ordem_parada, ordemOtimizada: null, naOtimizada: false });
      });
      otimizada.paradas.forEach((p) => {
        const k = String(p.id_cerca);
        const atual = porCerca.get(k);
        if (atual) Object.assign(atual, { ordemOtimizada: p.ordem_otimizada, naOtimizada: true });
        else porCerca.set(k, { ...p, ordemEsperada: null, ordemOtimizada: p.ordem_otimizada, naOtimizada: true });
      });
      const temOtimizada = !!(o && o.geojson);
      const nomes = this.nomesCercas();
      const paradas = [...porCerca.values()].map((p) => ({
        ...p,
        // Cerca da esperada que a otimização não atendeu fica destacada
        cor: temOtimizada && !p.naOtimizada ? COR_NAO_ATENDIDA : COR_SOBREPOSTA_PARADA,
        label: [
          nomes[p.id_cerca] || p.nome_cerca || `#${p.id_cerca}`,
          `${this.t("expected")}: ${p.ordemEsperada != null ? p.ordemEsperada : "—"}`,
          `${this.t("optimized")}: ${p.ordemOtimizada != null ? p.ordemOtimizada : "—"}`,
        ].join(" · "),
      }));

      const km = this.kmPorColuna;
      const infos = [
        { label: this.t("distanceExpected"), valor: km.esperada != null ? `${km.esperada.toFixed(1)} km` : "—" },
        { label: this.t("distanceOptimized"), valor: km.otimizada != null ? `${km.otimizada.toFixed(1)} km` : "—" },
      ];
      if (km.esperada && km.otimizada != null) {
        const diff = km.otimizada - km.esperada;
        const pct = (diff / km.esperada) * 100;
        infos.push({
          label: this.t("distanceDiff"),
          valor: `${diff > 0 ? "+" : ""}${diff.toFixed(1)} km (${pct > 0 ? "+" : ""}${pct.toFixed(1)}%)`,
        });
      }
      infos.push({ label: this.t("distanceRealized"), valor: km.realizada != null ? `${km.realizada.toFixed(1)} km` : "—" });
      infos.push(...this.infosCusto("otimizada").filter((i) => i.label === this.t("costDiffExpected")));

      return {
        key: "sobreposta",
        titulo: this.t("overlayTitle"),
        legenda: [
          { camada: "esperada", titulo: this.t("expected"), cor: COR_SOBREPOSTA_ESPERADA, tracejada: true },
          { camada: "otimizada", titulo: this.t("optimized"), cor: COR_SOBREPOSTA_OTIMIZADA, larga: true },
          ...(realizada.polyline.length > 1
            ? [{ camada: "realizada", titulo: this.t("realized"), cor: COR_SOBREPOSTA_REALIZADA }]
            : []),
          ...(paradas.length
            ? [{
                camada: "paradas",
                titulo: this.t("stops"),
                cor: COR_SOBREPOSTA_PARADA,
                ponto: true,
                extra: temOtimizada && paradas.some((p) => !p.naOtimizada)
                  ? { titulo: this.t("overlayUnassignedStop"), cor: COR_NAO_ATENDIDA }
                  : null,
              }]
            : []),
        ],
        cor: COR_SOBREPOSTA_OTIMIZADA,
        infos,
        aviso: otimizada.aviso,
        geojsons: geojsons.filter((g) => visivel[g.camada]),
        polyline: [],
        paradas: visivel.paradas ? paradas : [],
        vazio: [esperada.vazio, otimizada.vazio, realizada.vazio].filter(Boolean).join(" · ") || null,
      };
    },
    mudaViagem(delta) {
      this.indiceViagem = Math.max(0, this.indiceViagem + delta);
      this.load();
    },
    async load() {
      if (!this.chaveCarga) return;
      const chave = this.chaveCarga;
      const seq = ++this.requestSeq;
      this.loading = true;
      try {
        const res = await apiController.request(apiController.endpoints.getComparacaorotaslinha, {
          data: { id_linha: this.linha.id_linha, id_sentido: this.idSentido, indice: this.indiceViagem },
          silent: true,
        });
        // Resposta de uma linha/sentido que o usuário já deixou para trás
        if (seq !== this.requestSeq) return;
        this.dados = res.data || null;
        this.carregadoPara = chave;
        this.ajustaTodos();
        // Lotação alvo depende das cercas do sentido: tarifas vêm depois dos dados
        this.carregaCusto(chave);
      } catch (e) {
        if (seq !== this.requestSeq) return;
        this.dados = null;
        EventBus.$emit("show-error", { error: e });
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    ajustaTodos() {
      this.$nextTick(() => {
        // O v-tab-item anima a entrada: espera o painel ter tamanho antes de medir
        setTimeout(() => this.colunasVisiveis.forEach((c) => this.ajustaMapa(c.key)), 250);
      });
    },
    ajustaMapa(key) {
      const ref = this.$refs[`map_${key}`];
      const comp = Array.isArray(ref) ? ref[0] : ref;
      const map = comp && comp.mapObject;
      if (!map) return;
      map.invalidateSize();
      const coluna = this.colunasVisiveis.find((c) => c.key === key);
      if (!coluna) return;
      const bounds = L.latLngBounds([]);
      coluna.polyline.forEach((p) => bounds.extend(p));
      coluna.paradas.forEach((p) => bounds.extend([p.lat, p.lng]));
      coluna.geojsons.forEach((g) => {
        try {
          const b = L.geoJSON(g.geojson).getBounds();
          if (b.isValid()) bounds.extend(b);
        } catch (_) {
          /* geojson inválido: ignora no enquadramento */
        }
      });
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20] });
    },
    clearPoll() {
      if (this.pollTimer != null) clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.pollInFlight = false;
    },
    falhaGeracao(msg) {
      this.clearPoll();
      this.gerando = false;
      EventBus.$emit("show-alert", { mensagem: msg || this.t("generateError"), tipo: "error" });
    },
    /**
     * O scheduler grava em `mensagem_scheduler` uma chave técnica (ex.: MSG_BLOQUEIO_ALLOWLIST);
     * aqui ela vira um texto que diz o que fazer. Mesmo padrão de validacaoSchedulerMixin.
     */
    mensagemFalhaScheduler(processo) {
      const chave = processo && processo.mensagem_scheduler;
      if (chave === "MSG_BLOQUEIO_ALLOWLIST") return this.t("generateBlocked");
      if (chave === "MSG_INTERRUPCAO_SIGTERM" || chave === "MSG_INTERRUPCAO_SIGTERM_FALHOU") {
        return this.t("generateInterrupted");
      }
      return this.t("generateError");
    },
    async gerarOtimizacao() {
      if (!this.linha || !this.linha.id_linha || this.gerando) return;
      this.gerando = true;
      let idProcesso = null;
      try {
        const res = await apiController.request(apiController.endpoints.geraRotaotimizadalinha, {
          data: { id_linha: this.linha.id_linha },
          silent: true,
        });
        idProcesso = res.data && res.data.id_processo;
      } catch (e) {
        const data = e.response && e.response.data;
        return this.falhaGeracao(data && (data.message || data.mensagem));
      }
      if (!idProcesso) return this.falhaGeracao();
      this.pollProcesso(idProcesso);
    },
    pollProcesso(idProcesso) {
      this.clearPoll();
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      const pendingDeadline = Date.now() + PENDING_TIMEOUT_MS;
      const pollOnce = () => {
        if (Date.now() > deadline) return this.falhaGeracao();
        if (this.pollInFlight) return;
        this.pollInFlight = true;
        apiController.request(apiController.endpoints.schedulerGetProcessoschedulerbyid, {
          data: { id_processo: idProcesso },
          config: { timeout: 30000 },
          silent: true,
          onSuccess: (statusRes) => {
            this.pollInFlight = false;
            const pollData = statusRes.data || {};
            if (pollData.erro) return this.falhaGeracao(pollData.mensagem);
            const processo = pollData.processo || {};
            if (processo.status === "PENDING") {
              if (Date.now() > pendingDeadline) this.falhaGeracao(this.t("generateNotStarted"));
              return;
            }
            if (processo.status === "EXECUTING") return;
            if (processo.status !== "DONE") return this.falhaGeracao(this.mensagemFalhaScheduler(processo));
            this.clearPoll();
            this.gerando = false;
            EventBus.$emit("show-alert", { mensagem: this.t("generated"), tipo: "success" });
            this.load();
          },
          onError: (e) => {
            const data = e.response && e.response.data;
            this.falhaGeracao((data && (data.message || data.mensagem)) || e.message);
          },
        });
      };
      this.pollTimer = setInterval(pollOnce, POLL_MS);
      pollOnce();
    },
  },
};
</script>

<style scoped>
.linha-comparacao-rotas__swatch {
  display: inline-block;
  width: 14px;
  height: 4px;
  border-radius: 2px;
}
.linha-comparacao-rotas__swatch--larga {
  height: 8px;
  opacity: 0.75;
}
.linha-comparacao-rotas__info {
  font-size: 12px;
  line-height: 1.6;
}
.linha-comparacao-rotas__map {
  position: relative;
  height: 420px;
  flex-grow: 1;
}
.linha-comparacao-rotas__map--sobreposto {
  height: 600px;
}
/* Fundo em cinza: as cores do OSM (parques, vias) não competem com as duas rotas */
.linha-comparacao-rotas__map--sobreposto >>> .leaflet-tile-pane {
  filter: grayscale(1) brightness(1.05) contrast(0.85);
}
.linha-comparacao-rotas__sentido {
  max-width: 260px;
}
.linha-comparacao-rotas--fullscreen {
  min-height: 100%;
}
.linha-comparacao-rotas--fullscreen .linha-comparacao-rotas__map {
  height: calc(100vh - 230px);
  min-height: 320px;
}
.linha-comparacao-rotas__indicador {
  padding-top: 0 !important;
  padding-bottom: 2px !important;
}
.linha-comparacao-rotas__indicador .title {
  line-height: 1.3;
}
.linha-comparacao-rotas__indicador + .linha-comparacao-rotas__indicador {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
.linha-comparacao-rotas__legenda {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 500;
  background: rgba(255, 255, 255, 0.95) !important;
}
.linha-comparacao-rotas__legenda-switch >>> .v-label {
  display: flex;
  align-items: center;
}
.linha-comparacao-rotas__legenda-extra {
  padding-left: 54px;
}
.linha-comparacao-rotas__ponto {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid;
  background: #ffffff;
}
.linha-comparacao-rotas__vazio {
  position: absolute;
  left: 50%;
  top: 12px;
  transform: translateX(-50%);
  z-index: 500;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 4px;
  padding: 4px 10px;
  max-width: 90%;
  text-align: center;
}
</style>
